import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, ArrowPathIcon, ExclamationTriangleIcon, PencilIcon, SparklesIcon, TrashIcon, GlobeAltIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import Header from '../components/Header';
import { api, Product } from '../services/api';
import toast from 'react-hot-toast';
import GenericEntityEditor from '../components/admin/GenericEntityEditor';
import { productFieldDefinitions, componentFieldDefinitions } from '../components/admin/fieldDefinitions';
import { API_ENDPOINTS } from '../config/environment';
import RateLimitStats from '../components/RateLimitStats';

interface AdminLog {
  id: string;
  action_type: string;
  entity_type: string;
  entity_id?: string;
  entity_name?: string;
  description: string;
  changes_summary?: string;
  user_id?: string;
  api_endpoint?: string;
  request_method?: string;
  response_status?: number;
  processing_time_ms?: number;
  is_revertible: boolean;
  reverted_at?: string;
  revert_reason?: string;
  is_reverted: boolean;
  can_revert: boolean;
  created_at: string;
}

interface SystemStats {
  totals: {
    products: number;
    components: number;
    processes: number;
    jobs: number;
  };
  analysis: {
    completed_products: number;
    analyzing_products: number;
    completion_rate: number;
  };
  activity: {
    recent_actions_24h: number;
  };
  updated_at: string;
}

interface Component {
  id: string;
  name: string;
  slug: string;
  category: string;
  description?: string;
  country_of_origin?: string;
  production_locations?: any[];
  environmental_impact?: {
    carbon_footprint_kg?: number;
    energy_consumption_kwh?: number;
    water_usage_liters?: number;
    waste_generation_kg?: number;
    recyclability_percentage?: number;
    renewable_energy_percentage?: number;
  };
  labor_requirements?: {
    worker_count?: number;
    skill_level?: string;
    average_wage_usd_hour?: number;
    working_hours_per_week?: number;
    working_conditions?: string;
    safety_rating?: string;
    automation_level?: string;
  };
  design_rationale?: string;
}



const AdminDashboard = () => {
  const navigate = useNavigate();
  const [logs, setLogs] = useState<AdminLog[]>([]);
  const [systemStats, setSystemStats] = useState<SystemStats | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [components, setComponents] = useState<Component[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedTab, setSelectedTab] = useState<'products' | 'logs' | 'stats' | 'environmental' | 'rate-limits'>('products');
  const [filterActionType, setFilterActionType] = useState<string>('');
  const [filterEntityType, setFilterEntityType] = useState<string>('');
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [editingComponent, setEditingComponent] = useState<Component | null>(null);
  const [editingProductEnvData, setEditingProductEnvData] = useState<Product | null>(null);
  const [showGenericEditor, setShowGenericEditor] = useState(false);
  const [genericEditorConfig, setGenericEditorConfig] = useState<any>(null);
  const [isAnalyzing, setIsAnalyzing] = useState<Set<string>>(new Set());

  const loadData = async (showRefreshToast = false) => {
    try {
      setIsLoading(true);
      
      const [logsData, statsData, productsData, componentsResponse] = await Promise.all([
        api.getAdminLogs({ 
          limit: 50, 
          action_type: filterActionType || undefined, 
          entity_type: filterEntityType || undefined 
        }),
        api.getAdminStats(),
        api.getProducts({ limit: 100 }),
        fetch(`${API_ENDPOINTS.components}?limit=100`)
      ]);
      
      setLogs(logsData);
      setSystemStats(statsData);
      setProducts(productsData);
      
      if (componentsResponse.ok) {
        const componentsData = await componentsResponse.json();
        setComponents(componentsData);
      }
      
      if (showRefreshToast) {
        toast.success('Admin data refreshed');
      }
    } catch (error) {
      console.error('Failed to load admin data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [filterActionType, filterEntityType]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await loadData(true);
  };

  const handleRevert = async (logId: string, reason: string) => {
    try {
      await api.revertAdminAction(logId, reason, 'admin_user');
      toast.success('Action reverted successfully');
      await loadData();
    } catch (error) {
      console.error('Failed to revert action:', error);
      toast.error('Failed to revert action');
    }
  };

  const handleEditProduct = (product: Product) => {
    setGenericEditorConfig({
      entityType: 'product',
      entityId: product.id,
      entityData: product,
      fieldDefinitions: productFieldDefinitions,
              title: `Edit Product: ${product.display_name}`,
        apiEndpoint: API_ENDPOINTS.admin.updateProduct(product.id),
      showVersions: true,
      showEnvironmental: true,
      showSubEntities: true,
      subEntityType: 'Component'
    });
    setShowGenericEditor(true);
  };

  const handleEditProductEnvData = (product: Product) => {
    setEditingProductEnvData({ ...product });
  };

  const handleEditComponent = (component: Component) => {
    setGenericEditorConfig({
      entityType: 'component',
      entityId: component.id,
      entityData: component,
      fieldDefinitions: componentFieldDefinitions,
              title: `Edit Component: ${component.name}`,
        apiEndpoint: API_ENDPOINTS.admin.updateComponent(component.id),
      showVersions: true,
      showEnvironmental: true,
      showSubEntities: true,
      subEntityType: 'Sub-Component'
    });
    setShowGenericEditor(true);
  };

  const handleGenericSave = async (data: any) => {
    if (!genericEditorConfig) return;

    try {
      if (genericEditorConfig.entityType === 'product') {
        await api.updateProductGeneric(
          genericEditorConfig.entityId, 
          data, 
          'Updated via admin interface', 
          'admin_user'
        );
      } else if (genericEditorConfig.entityType === 'component') {
        await api.updateComponentGeneric(
          genericEditorConfig.entityId, 
          data, 
          'Updated via admin interface', 
          'admin_user'
        );
      }

      setShowGenericEditor(false);
      setGenericEditorConfig(null);
      await loadData();
    } catch (error) {
      console.error('Failed to save:', error);
      throw error; // Re-throw to let the editor handle the error display
    }
  };

  const handleSaveProduct = async () => {
    if (!editingProduct) return;

    try {
      // Update product via API
      const response = await fetch(`${API_ENDPOINTS.products}/${editingProduct.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: editingProduct.name,
          brand: editingProduct.brand,
          category: editingProduct.category,
          retail_price: editingProduct.retail_price,
          manufacturing_cost: editingProduct.manufacturing_cost,
          description: editingProduct.description
        }),
      });

      if (response.ok) {
        toast.success('Product updated successfully');
        setEditingProduct(null);
        await loadData();
      } else {
        const error = await response.json();
        toast.error(`Failed to update product: ${error.detail}`);
      }
    } catch (error) {
      console.error('Failed to update product:', error);
      toast.error('Failed to update product');
    }
  };

  const handleSaveProductEnvData = async () => {
    if (!editingProductEnvData) return;

    try {
      const updateData = {
        overall_environmental_impact: {
          carbon_footprint_kg: editingProductEnvData.overall_environmental_impact?.carbon_footprint_kg,
          energy_consumption_kwh: editingProductEnvData.overall_environmental_impact?.energy_consumption_kwh,
          water_usage_liters: editingProductEnvData.overall_environmental_impact?.water_usage_liters,
          waste_generation_kg: editingProductEnvData.overall_environmental_impact?.waste_generation_kg,
          recyclability_percentage: editingProductEnvData.overall_environmental_impact?.recyclability_percentage
        },
        overall_labor_summary: editingProductEnvData.overall_labor_summary,
        total_worker_count: editingProductEnvData.total_worker_count,
        primary_production_countries: editingProductEnvData.primary_production_countries
      };

      await api.updateProductData(editingProductEnvData.id, updateData);
      toast.success('Product environmental and labor data updated successfully');
      setEditingProductEnvData(null);
      await loadData();
    } catch (error) {
      console.error('Failed to update product environmental data:', error);
      toast.error('Failed to update product environmental data');
    }
  };

  const handleSaveComponent = async () => {
    if (!editingComponent) return;

    try {
      const updateData = {
        country_of_origin: editingComponent.country_of_origin,
        production_locations: editingComponent.production_locations,
        environmental_impact: editingComponent.environmental_impact,
        labor_requirements: editingComponent.labor_requirements,
        design_rationale: editingComponent.design_rationale
      };

      await api.updateComponentData(editingComponent.id, updateData);
      toast.success('Component data updated successfully');
      setEditingComponent(null);
      await loadData();
    } catch (error) {
      console.error('Failed to update component:', error);
      toast.error('Failed to update component');
    }
  };

  const handleDeleteProduct = async (productId: string, productName: string) => {
    if (!confirm(`Are you sure you want to delete "${productName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await fetch(`${API_ENDPOINTS.products}/${productId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        toast.success('Product deleted successfully');
        await loadData();
      } else {
        const error = await response.json();
        toast.error(`Failed to delete product: ${error.detail}`);
      }
    } catch (error) {
      console.error('Failed to delete product:', error);
      toast.error('Failed to delete product');
    }
  };

  const handleRefreshAnalysis = async (productId: string, productName: string) => {
    if (!confirm(`Re-analyze "${productName}" with AI? This will update all component data.`)) {
      return;
    }

    try {
      setIsAnalyzing(prev => new Set(prev).add(productId));
      
      const response = await fetch(API_ENDPOINTS.productRefreshAnalysis(productId), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        toast.success('Analysis started! Check the product page for progress.');
        await loadData();
      } else {
        const error = await response.json();
        toast.error(`Failed to start analysis: ${error.detail}`);
      }
    } catch (error) {
      console.error('Failed to start analysis:', error);
      toast.error('Failed to start analysis');
    } finally {
      setIsAnalyzing(prev => {
        const newSet = new Set(prev);
        newSet.delete(productId);
        return newSet;
      });
    }
  };

  const getActionTypeColor = (actionType: string) => {
    switch (actionType) {
      case 'create': return 'text-green-600 bg-green-100';
      case 'update': return 'text-blue-600 bg-blue-100';
      case 'delete': return 'text-red-600 bg-red-100';
      case 'analyze': return 'text-purple-600 bg-purple-100';
      case 'revert': return 'text-orange-600 bg-orange-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getEntityTypeIcon = (entityType: string) => {
    switch (entityType) {
      case 'product': return '📱';
      case 'component': return '🔧';
      case 'process': return '🏭';
      case 'job': return '⚙️';
      default: return '📄';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <>
      <title>Admin Dashboard - ProductTrace</title>
      <meta name="description" content="Admin dashboard for ProductTrace system management" />

      <div className="min-h-screen bg-gray-50">
        <Header />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/')}
                className="flex items-center text-gray-600 hover:text-gray-900 transition-colors mr-4"
              >
                <ArrowLeftIcon className="w-4 h-4 mr-2" />
                Back to Home
              </button>
              <h1 className="text-3xl font-bold text-gray-900">
                🛠️ Admin Dashboard
              </h1>
            </div>
            
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              {isRefreshing ? (
                <>
                  <div className="animate-spin w-4 h-4 mr-2 border-2 border-gray-400 border-t-transparent rounded-full" />
                  Refreshing...
                </>
              ) : (
                <>
                  <ArrowPathIcon className="w-4 h-4 mr-2" />
                  Refresh
                </>
              )}
            </button>
          </div>

          {/* Tab Navigation */}
          <div className="border-b border-gray-200 mb-8">
            <nav className="flex space-x-8">
              <button
                onClick={() => setSelectedTab('products')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  selectedTab === 'products'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                📱 Product Management
              </button>
              <button
                onClick={() => setSelectedTab('environmental')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  selectedTab === 'environmental'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                🌱 Environmental & Labor Data
              </button>
              <button
                onClick={() => setSelectedTab('logs')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  selectedTab === 'logs'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                📋 Activity Logs
              </button>
              <button
                onClick={() => setSelectedTab('stats')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  selectedTab === 'stats'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                📊 System Stats
              </button>
              <button
                onClick={() => setSelectedTab('rate-limits')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  selectedTab === 'rate-limits'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                🚦 Rate Limits
              </button>
            </nav>
          </div>

          {/* Environmental & Labor Data Tab */}
          {selectedTab === 'environmental' && (
            <div className="space-y-6">
              {/* Product Environmental Data */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <GlobeAltIcon className="w-5 h-5 mr-2 text-green-600" />
                    Product Environmental & Labor Data
                  </h3>
                  <div className="text-sm text-gray-600">
                    {products.length} products
                  </div>
                </div>
                
                {isLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
                    <span className="ml-3 text-gray-600">Loading products...</span>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {products.map((product) => (
                      <div key={product.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <h4 className="font-medium text-gray-900">{product.display_name}</h4>
                            <p className="text-sm text-gray-600">{product.category}</p>
                          </div>
                          <button
                            onClick={() => handleEditProductEnvData(product)}
                            className="px-3 py-1 text-sm text-blue-600 border border-blue-300 rounded hover:bg-blue-50 transition-colors"
                          >
                            <PencilIcon className="w-4 h-4 inline mr-1" />
                            Edit Environmental Data
                          </button>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                          <div className="bg-green-50 p-3 rounded">
                            <h5 className="font-medium text-green-800 mb-2">Environmental Impact</h5>
                            <div className="space-y-1 text-green-700">
                              <div>Carbon: {product.overall_environmental_impact?.carbon_footprint_kg || 'N/A'} kg CO₂</div>
                              <div>Energy: {product.overall_environmental_impact?.energy_consumption_kwh || 'N/A'} kWh</div>
                              <div>Water: {product.overall_environmental_impact?.water_usage_liters || 'N/A'} L</div>
                            </div>
                          </div>
                          
                          <div className="bg-blue-50 p-3 rounded">
                            <h5 className="font-medium text-blue-800 mb-2">Labor Information</h5>
                            <div className="space-y-1 text-blue-700">
                              <div>Workers: {product.total_worker_count || 'N/A'}</div>
                              <div>Countries: {product.primary_production_countries?.length || 0}</div>
                              <div className="text-xs">{product.overall_labor_summary ? product.overall_labor_summary.substring(0, 50) + '...' : 'No summary'}</div>
                            </div>
                          </div>
                          
                          <div className="bg-gray-50 p-3 rounded">
                            <h5 className="font-medium text-gray-800 mb-2">Production Countries</h5>
                            <div className="space-y-1 text-gray-700 text-xs">
                              {product.primary_production_countries?.slice(0, 3).map((country, idx) => (
                                <div key={idx}>{country.country}: {country.percentage}%</div>
                              )) || <div>No data</div>}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Component Environmental Data */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <UserGroupIcon className="w-5 h-5 mr-2 text-blue-600" />
                    Component Environmental & Labor Data
                  </h3>
                  <div className="text-sm text-gray-600">
                    {components.length} components
                  </div>
                </div>
                
                {isLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
                    <span className="ml-3 text-gray-600">Loading components...</span>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {components.map((component) => (
                      <div key={component.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <h4 className="font-medium text-gray-900">{component.name}</h4>
                            <p className="text-sm text-gray-600">{component.category} • {component.country_of_origin || 'Unknown origin'}</p>
                          </div>
                          <button
                            onClick={() => handleEditComponent(component)}
                            className="px-3 py-1 text-sm text-blue-600 border border-blue-300 rounded hover:bg-blue-50 transition-colors"
                          >
                            <PencilIcon className="w-4 h-4 inline mr-1" />
                            Edit Component Data
                          </button>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                          <div className="bg-green-50 p-3 rounded">
                            <h5 className="font-medium text-green-800 mb-2">Environmental</h5>
                            <div className="space-y-1 text-green-700">
                              <div>Carbon: {component.environmental_impact?.carbon_footprint_kg || 'N/A'} kg</div>
                              <div>Energy: {component.environmental_impact?.energy_consumption_kwh || 'N/A'} kWh</div>
                              <div>Recyclable: {component.environmental_impact?.recyclability_percentage || 'N/A'}%</div>
                            </div>
                          </div>
                          
                          <div className="bg-blue-50 p-3 rounded">
                            <h5 className="font-medium text-blue-800 mb-2">Labor</h5>
                            <div className="space-y-1 text-blue-700">
                              <div>Workers: {component.labor_requirements?.worker_count || 'N/A'}</div>
                              <div>Skill: {component.labor_requirements?.skill_level || 'N/A'}</div>
                              <div>Wage: ${component.labor_requirements?.average_wage_usd_hour || 'N/A'}/hr</div>
                            </div>
                          </div>
                          
                          <div className="bg-gray-50 p-3 rounded">
                            <h5 className="font-medium text-gray-800 mb-2">Production</h5>
                            <div className="space-y-1 text-gray-700 text-xs">
                              <div>Origin: {component.country_of_origin || 'Unknown'}</div>
                              <div>Locations: {component.production_locations?.length || 0}</div>
                              <div>Conditions: {component.labor_requirements?.working_conditions || 'N/A'}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    {components.length === 0 && (
                      <div className="text-center py-12">
                        <div className="text-gray-400 text-4xl mb-4">🔧</div>
                        <p className="text-gray-600">No components found.</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Product Management Tab */}
          {selectedTab === 'products' && (
            <div className="space-y-6">
              {/* Product List */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">Product Management</h3>
                  <div className="text-sm text-gray-600">
                    {products.length} products total
                  </div>
                </div>
                
                {isLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
                    <span className="ml-3 text-gray-600">Loading products...</span>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {products.map((product) => (
                      <div key={product.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <h4 className="font-medium text-gray-900">{product.display_name}</h4>
                              <span className={`px-2 py-1 rounded text-xs ${
                                product.analysis_status === 'completed' ? 'bg-green-100 text-green-800' :
                                product.analysis_status === 'analyzing' ? 'bg-blue-100 text-blue-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {product.analysis_status}
                              </span>
                              <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
                                {product.category}
                              </span>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm text-gray-600">
                              <div>
                                <strong>Retail:</strong> ${product.retail_price || 'N/A'}
                              </div>
                              <div>
                                <strong>Manufacturing:</strong> ${product.manufacturing_cost || 'N/A'}
                              </div>
                              <div>
                                <strong>Components:</strong> {product.analyzed_components}/{product.total_components}
                              </div>
                              <div>
                                <strong>Progress:</strong> {product.analysis_progress}%
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex items-center space-x-2 ml-4">
                            <button
                              onClick={() => navigate(`/products/${product.slug}`)}
                              className="px-3 py-1 text-sm text-blue-600 border border-blue-300 rounded hover:bg-blue-50 transition-colors"
                            >
                              View
                            </button>
                            <button
                              onClick={() => handleEditProduct(product)}
                              className="px-3 py-1 text-sm text-gray-600 border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                              title="Edit with Advanced Editor"
                            >
                              <PencilIcon className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleRefreshAnalysis(product.id, product.name)}
                              disabled={isAnalyzing.has(product.id)}
                              className="px-3 py-1 text-sm text-purple-600 border border-purple-300 rounded hover:bg-purple-50 transition-colors disabled:opacity-50"
                            >
                              {isAnalyzing.has(product.id) ? (
                                <div className="animate-spin w-4 h-4 border-2 border-purple-600 border-t-transparent rounded-full" />
                              ) : (
                                <SparklesIcon className="w-4 h-4" />
                              )}
                            </button>
                            <button
                              onClick={() => handleDeleteProduct(product.id, product.name)}
                              className="px-3 py-1 text-sm text-red-600 border border-red-300 rounded hover:bg-red-50 transition-colors"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    {products.length === 0 && (
                      <div className="text-center py-12">
                        <div className="text-gray-400 text-4xl mb-4">📱</div>
                        <p className="text-gray-600">No products found.</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* System Stats Tab */}
          {selectedTab === 'stats' && systemStats && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-white p-6 rounded-lg border border-gray-200">
                  <div className="text-2xl font-bold text-blue-600 mb-2">
                    {systemStats.totals.products.toLocaleString()}
                  </div>
                  <div className="text-sm text-gray-600">Total Products</div>
                </div>
                
                <div className="bg-white p-6 rounded-lg border border-gray-200">
                  <div className="text-2xl font-bold text-green-600 mb-2">
                    {systemStats.totals.components.toLocaleString()}
                  </div>
                  <div className="text-sm text-gray-600">Total Components</div>
                </div>
                
                <div className="bg-white p-6 rounded-lg border border-gray-200">
                  <div className="text-2xl font-bold text-purple-600 mb-2">
                    {systemStats.totals.processes.toLocaleString()}
                  </div>
                  <div className="text-sm text-gray-600">Manufacturing Processes</div>
                </div>
                
                <div className="bg-white p-6 rounded-lg border border-gray-200">
                  <div className="text-2xl font-bold text-orange-600 mb-2">
                    {systemStats.analysis.completion_rate}%
                  </div>
                  <div className="text-sm text-gray-600">Analysis Completion</div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Analysis Status</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <div className="text-xl font-bold text-green-600 mb-1">
                      {systemStats.analysis.completed_products}
                    </div>
                    <div className="text-sm text-gray-600">Completed Products</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xl font-bold text-blue-600 mb-1">
                      {systemStats.analysis.analyzing_products}
                    </div>
                    <div className="text-sm text-gray-600">Currently Analyzing</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xl font-bold text-gray-600 mb-1">
                      {systemStats.activity.recent_actions_24h}
                    </div>
                    <div className="text-sm text-gray-600">Actions (24h)</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Rate Limits Tab */}
          {selectedTab === 'rate-limits' && (
            <div className="space-y-6">
              <RateLimitStats />
            </div>
          )}

          {/* Activity Logs Tab */}
          {selectedTab === 'logs' && (
            <div className="space-y-6">
              {/* Filters */}
              <div className="bg-white p-4 rounded-lg border border-gray-200">
                <div className="flex flex-wrap gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Action Type
                    </label>
                    <select
                      value={filterActionType}
                      onChange={(e) => setFilterActionType(e.target.value)}
                      className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                    >
                      <option value="">All Actions</option>
                      <option value="create">Create</option>
                      <option value="update">Update</option>
                      <option value="delete">Delete</option>
                      <option value="analyze">Analyze</option>
                      <option value="revert">Revert</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Entity Type
                    </label>
                    <select
                      value={filterEntityType}
                      onChange={(e) => setFilterEntityType(e.target.value)}
                      className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                    >
                      <option value="">All Entities</option>
                      <option value="product">Products</option>
                      <option value="component">Components</option>
                      <option value="process">Processes</option>
                      <option value="job">Jobs</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Logs List */}
              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
                  <span className="ml-3 text-gray-600">Loading admin logs...</span>
                </div>
              ) : (
                <div className="space-y-4">
                  {logs.map((log) => (
                    <div
                      key={log.id}
                      className={`bg-white p-6 rounded-lg border border-gray-200 ${
                        log.is_reverted ? 'opacity-60' : ''
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <span className="text-lg">{getEntityTypeIcon(log.entity_type)}</span>
                            <span className={`px-2 py-1 rounded text-xs font-medium ${getActionTypeColor(log.action_type)}`}>
                              {log.action_type.toUpperCase()}
                            </span>
                            <span className="text-sm text-gray-500">{log.entity_type}</span>
                            {log.entity_name && (
                              <span className="text-sm font-medium text-gray-900">
                                {log.entity_name}
                              </span>
                            )}
                          </div>
                          
                          <p className="text-gray-700 mb-2">{log.description}</p>
                          
                          {log.changes_summary && (
                            <p className="text-sm text-gray-600 mb-2">
                              Changes: {log.changes_summary}
                            </p>
                          )}
                          
                          <div className="flex items-center space-x-4 text-xs text-gray-500">
                            <span>{formatDate(log.created_at)}</span>
                            {log.api_endpoint && (
                              <span>{log.request_method} {log.api_endpoint}</span>
                            )}
                            {log.processing_time_ms && (
                              <span>{log.processing_time_ms}ms</span>
                            )}
                          </div>
                          
                          {log.is_reverted && (
                            <div className="mt-2 p-2 bg-orange-50 border border-orange-200 rounded text-sm">
                              <div className="flex items-center text-orange-700">
                                <ExclamationTriangleIcon className="w-4 h-4 mr-1" />
                                Reverted: {log.revert_reason}
                              </div>
                              <div className="text-orange-600 text-xs mt-1">
                                Reverted at: {log.reverted_at ? formatDate(log.reverted_at) : 'Unknown'}
                              </div>
                            </div>
                          )}
                        </div>
                        
                        <div className="ml-4">
                          {log.can_revert && (
                            <button
                              onClick={() => {
                                const reason = prompt('Enter reason for reverting this action:');
                                if (reason) {
                                  handleRevert(log.id, reason);
                                }
                              }}
                              className="px-3 py-1 text-sm text-red-600 border border-red-300 rounded hover:bg-red-50 transition-colors"
                            >
                              Revert
                            </button>
                          )}
                          {log.is_reverted && (
                            <span className="px-3 py-1 text-sm text-orange-600 bg-orange-100 rounded">
                              Reverted
                            </span>
                          )}
                          {!log.is_revertible && !log.is_reverted && (
                            <span className="px-3 py-1 text-sm text-gray-500 bg-gray-100 rounded">
                              Non-revertible
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {logs.length === 0 && (
                    <div className="text-center py-12">
                      <div className="text-gray-400 text-4xl mb-4">📋</div>
                      <p className="text-gray-600">No admin logs found.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Product Environmental Data Edit Modal */}
      {editingProductEnvData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Edit Environmental & Labor Data</h3>
              <button
                onClick={() => setEditingProductEnvData(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            
            <div className="space-y-6">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-2">Product: {editingProductEnvData.display_name}</h4>
                <p className="text-sm text-gray-600">{editingProductEnvData.category}</p>
              </div>

              {/* Environmental Impact Section */}
              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-4 flex items-center">
                  <GlobeAltIcon className="w-5 h-5 mr-2 text-green-600" />
                  Environmental Impact
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Carbon Footprint (kg CO₂)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={editingProductEnvData.overall_environmental_impact?.carbon_footprint_kg || ''}
                      onChange={(e) => setEditingProductEnvData({
                        ...editingProductEnvData,
                        overall_environmental_impact: {
                          ...editingProductEnvData.overall_environmental_impact,
                          carbon_footprint_kg: parseFloat(e.target.value) || undefined
                        }
                      })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Energy Consumption (kWh)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={editingProductEnvData.overall_environmental_impact?.energy_consumption_kwh || ''}
                      onChange={(e) => setEditingProductEnvData({
                        ...editingProductEnvData,
                        overall_environmental_impact: {
                          ...editingProductEnvData.overall_environmental_impact,
                          energy_consumption_kwh: parseFloat(e.target.value) || undefined
                        }
                      })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Water Usage (Liters)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={editingProductEnvData.overall_environmental_impact?.water_usage_liters || ''}
                      onChange={(e) => setEditingProductEnvData({
                        ...editingProductEnvData,
                        overall_environmental_impact: {
                          ...editingProductEnvData.overall_environmental_impact,
                          water_usage_liters: parseFloat(e.target.value) || undefined
                        }
                      })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Waste Generation (kg)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={editingProductEnvData.overall_environmental_impact?.waste_generation_kg || ''}
                      onChange={(e) => setEditingProductEnvData({
                        ...editingProductEnvData,
                        overall_environmental_impact: {
                          ...editingProductEnvData.overall_environmental_impact,
                          waste_generation_kg: parseFloat(e.target.value) || undefined
                        }
                      })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Recyclability (%)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      max="100"
                      value={editingProductEnvData.overall_environmental_impact?.recyclability_percentage || ''}
                      onChange={(e) => setEditingProductEnvData({
                        ...editingProductEnvData,
                        overall_environmental_impact: {
                          ...editingProductEnvData.overall_environmental_impact,
                          recyclability_percentage: parseFloat(e.target.value) || undefined
                        }
                      })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Labor Information Section */}
              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-4 flex items-center">
                  <UserGroupIcon className="w-5 h-5 mr-2 text-blue-600" />
                  Labor Information
                </h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Total Worker Count
                    </label>
                    <input
                      type="number"
                      value={editingProductEnvData.total_worker_count || ''}
                      onChange={(e) => setEditingProductEnvData({
                        ...editingProductEnvData,
                        total_worker_count: parseInt(e.target.value) || undefined
                      })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Labor Summary
                    </label>
                    <textarea
                      value={editingProductEnvData.overall_labor_summary || ''}
                      onChange={(e) => setEditingProductEnvData({
                        ...editingProductEnvData,
                        overall_labor_summary: e.target.value
                      })}
                      rows={3}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      placeholder="Summary of labor conditions across the supply chain..."
                    />
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex items-center justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
              <button
                onClick={() => setEditingProductEnvData(null)}
                className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveProductEnvData}
                className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Save Environmental Data
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Component Edit Modal */}
      {editingComponent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Edit Component Data</h3>
              <button
                onClick={() => setEditingComponent(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            
            <div className="space-y-6">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-2">Component: {editingComponent.name}</h4>
                <p className="text-sm text-gray-600">{editingComponent.category}</p>
              </div>

              {/* Geographic Information */}
              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-4">Geographic Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Primary Country of Origin
                    </label>
                    <input
                      type="text"
                      value={editingComponent.country_of_origin || ''}
                      onChange={(e) => setEditingComponent({
                        ...editingComponent,
                        country_of_origin: e.target.value
                      })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      placeholder="e.g., China, Taiwan, Germany"
                    />
                  </div>
                </div>
              </div>

              {/* Environmental Impact */}
              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-4 flex items-center">
                  <GlobeAltIcon className="w-5 h-5 mr-2 text-green-600" />
                  Environmental Impact
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Carbon Footprint (kg CO₂)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={editingComponent.environmental_impact?.carbon_footprint_kg || ''}
                      onChange={(e) => setEditingComponent({
                        ...editingComponent,
                        environmental_impact: {
                          ...editingComponent.environmental_impact,
                          carbon_footprint_kg: parseFloat(e.target.value) || undefined
                        }
                      })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Energy Consumption (kWh)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={editingComponent.environmental_impact?.energy_consumption_kwh || ''}
                      onChange={(e) => setEditingComponent({
                        ...editingComponent,
                        environmental_impact: {
                          ...editingComponent.environmental_impact,
                          energy_consumption_kwh: parseFloat(e.target.value) || undefined
                        }
                      })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Water Usage (Liters)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={editingComponent.environmental_impact?.water_usage_liters || ''}
                      onChange={(e) => setEditingComponent({
                        ...editingComponent,
                        environmental_impact: {
                          ...editingComponent.environmental_impact,
                          water_usage_liters: parseFloat(e.target.value) || undefined
                        }
                      })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Waste Generation (kg)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={editingComponent.environmental_impact?.waste_generation_kg || ''}
                      onChange={(e) => setEditingComponent({
                        ...editingComponent,
                        environmental_impact: {
                          ...editingComponent.environmental_impact,
                          waste_generation_kg: parseFloat(e.target.value) || undefined
                        }
                      })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Recyclability (%)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      max="100"
                      value={editingComponent.environmental_impact?.recyclability_percentage || ''}
                      onChange={(e) => setEditingComponent({
                        ...editingComponent,
                        environmental_impact: {
                          ...editingComponent.environmental_impact,
                          recyclability_percentage: parseFloat(e.target.value) || undefined
                        }
                      })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Renewable Energy (%)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      max="100"
                      value={editingComponent.environmental_impact?.renewable_energy_percentage || ''}
                      onChange={(e) => setEditingComponent({
                        ...editingComponent,
                        environmental_impact: {
                          ...editingComponent.environmental_impact,
                          renewable_energy_percentage: parseFloat(e.target.value) || undefined
                        }
                      })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Labor Requirements */}
              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-4 flex items-center">
                  <UserGroupIcon className="w-5 h-5 mr-2 text-blue-600" />
                  Labor Requirements
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Worker Count
                    </label>
                    <input
                      type="number"
                      value={editingComponent.labor_requirements?.worker_count || ''}
                      onChange={(e) => setEditingComponent({
                        ...editingComponent,
                        labor_requirements: {
                          ...editingComponent.labor_requirements,
                          worker_count: parseInt(e.target.value) || undefined
                        }
                      })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Skill Level
                    </label>
                    <select
                      value={editingComponent.labor_requirements?.skill_level || ''}
                      onChange={(e) => setEditingComponent({
                        ...editingComponent,
                        labor_requirements: {
                          ...editingComponent.labor_requirements,
                          skill_level: e.target.value
                        }
                      })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    >
                      <option value="">Select skill level</option>
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="expert">Expert</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Average Wage (USD/hour)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={editingComponent.labor_requirements?.average_wage_usd_hour || ''}
                      onChange={(e) => setEditingComponent({
                        ...editingComponent,
                        labor_requirements: {
                          ...editingComponent.labor_requirements,
                          average_wage_usd_hour: parseFloat(e.target.value) || undefined
                        }
                      })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Working Hours/Week
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={editingComponent.labor_requirements?.working_hours_per_week || ''}
                      onChange={(e) => setEditingComponent({
                        ...editingComponent,
                        labor_requirements: {
                          ...editingComponent.labor_requirements,
                          working_hours_per_week: parseFloat(e.target.value) || undefined
                        }
                      })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Working Conditions
                    </label>
                    <select
                      value={editingComponent.labor_requirements?.working_conditions || ''}
                      onChange={(e) => setEditingComponent({
                        ...editingComponent,
                        labor_requirements: {
                          ...editingComponent.labor_requirements,
                          working_conditions: e.target.value
                        }
                      })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    >
                      <option value="">Select conditions</option>
                      <option value="poor">Poor</option>
                      <option value="fair">Fair</option>
                      <option value="good">Good</option>
                      <option value="excellent">Excellent</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Safety Rating
                    </label>
                    <select
                      value={editingComponent.labor_requirements?.safety_rating || ''}
                      onChange={(e) => setEditingComponent({
                        ...editingComponent,
                        labor_requirements: {
                          ...editingComponent.labor_requirements,
                          safety_rating: e.target.value
                        }
                      })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    >
                      <option value="">Select safety rating</option>
                      <option value="poor">Poor</option>
                      <option value="fair">Fair</option>
                      <option value="good">Good</option>
                      <option value="excellent">Excellent</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Automation Level
                    </label>
                    <select
                      value={editingComponent.labor_requirements?.automation_level || ''}
                      onChange={(e) => setEditingComponent({
                        ...editingComponent,
                        labor_requirements: {
                          ...editingComponent.labor_requirements,
                          automation_level: e.target.value
                        }
                      })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    >
                      <option value="">Select automation level</option>
                      <option value="manual">Manual</option>
                      <option value="semi-automated">Semi-automated</option>
                      <option value="automated">Automated</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Design Rationale */}
              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-4">Design Rationale</h4>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Design Rationale & Trade-offs
                  </label>
                  <textarea
                    value={editingComponent.design_rationale || ''}
                    onChange={(e) => setEditingComponent({
                      ...editingComponent,
                      design_rationale: e.target.value
                    })}
                    rows={3}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    placeholder="Why this component was chosen, trade-offs made, alternatives considered..."
                  />
                </div>
              </div>
            </div>
            
            <div className="flex items-center justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
              <button
                onClick={() => setEditingComponent(null)}
                className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveComponent}
                className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Save Component Data
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Product Edit Modal */}
      {editingProduct && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Edit Product</h3>
              <button
                onClick={() => setEditingProduct(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Product Name
                  </label>
                  <input
                    type="text"
                    value={editingProduct.name}
                    onChange={(e) => setEditingProduct({ ...editingProduct, name: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Brand
                  </label>
                  <input
                    type="text"
                    value={editingProduct.brand || ''}
                    onChange={(e) => setEditingProduct({ ...editingProduct, brand: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category
                  </label>
                  <select
                    value={editingProduct.category || ''}
                    onChange={(e) => setEditingProduct({ ...editingProduct, category: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  >
                    <option value="">Select category</option>
                    <option value="electronics">Electronics</option>
                    <option value="automotive">Automotive</option>
                    <option value="apparel">Apparel</option>
                    <option value="food">Food</option>
                    <option value="consumer_goods">Consumer Goods</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Retail Price ($)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={editingProduct.retail_price || ''}
                    onChange={(e) => setEditingProduct({ ...editingProduct, retail_price: parseFloat(e.target.value) || 0 })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Manufacturing Cost ($)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={editingProduct.manufacturing_cost || ''}
                    onChange={(e) => setEditingProduct({ ...editingProduct, manufacturing_cost: parseFloat(e.target.value) || 0 })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={editingProduct.description || ''}
                  onChange={(e) => setEditingProduct({ ...editingProduct, description: e.target.value })}
                  rows={3}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  placeholder="Product description..."
                />
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-2">Analysis Status</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Status:</span>
                    <div className="font-medium">{editingProduct.analysis_status}</div>
                  </div>
                  <div>
                    <span className="text-gray-600">Progress:</span>
                    <div className="font-medium">{editingProduct.analysis_progress}%</div>
                  </div>
                  <div>
                    <span className="text-gray-600">Components:</span>
                    <div className="font-medium">{editingProduct.analyzed_components}/{editingProduct.total_components}</div>
                  </div>
                  <div>
                    <span className="text-gray-600">Confidence:</span>
                    <div className="font-medium">{editingProduct.confidence_score || 'N/A'}%</div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex items-center justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
              <button
                onClick={() => setEditingProduct(null)}
                className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveProduct}
                className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Generic Entity Editor */}
      {showGenericEditor && genericEditorConfig && (
        <GenericEntityEditor
          entityType={genericEditorConfig.entityType}
          entityId={genericEditorConfig.entityId}
          entityData={genericEditorConfig.entityData}
          fieldDefinitions={genericEditorConfig.fieldDefinitions}
          onSave={handleGenericSave}
          onCancel={() => {
            setShowGenericEditor(false);
            setGenericEditorConfig(null);
          }}
          title={genericEditorConfig.title}
          apiEndpoint={genericEditorConfig.apiEndpoint}
          showVersions={genericEditorConfig.showVersions}
          showEnvironmental={genericEditorConfig.showEnvironmental}
          showSubEntities={genericEditorConfig.showSubEntities}
          subEntityType={genericEditorConfig.subEntityType}
        />
      )}
    </>
  );
};

export default AdminDashboard; 